import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTextStatusChange, isTextRevisionNotAllowedError } from 'utils/hooks/useTextStatusChange';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import { UserSessionSelector } from 'store/UserSession';
import { ROLES } from 'utils/Constants';
import './StatusDropdown.css';

export const StatusDropdown = ({
  textId,
  currentStatus,
  onStatusChange,
  onOptimisticUpdate,
  onError,
  previousItem,
  design = 'default',
  allowedStatusValues = null,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector(UserSessionSelector);
  const [openStatusMenuId, setOpenStatusMenuId] = useState(null);
  const [statusMenuCoords, setStatusMenuCoords] = useState({ top: 0, left: 0, width: 0 });
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [isRevisionErrorModalOpen, setIsRevisionErrorModalOpen] = useState(false);

  const isConsultant = user?.role === ROLES.CONSULTANT;

  const statusOptions = useMemo(() => {
    const allOptions = [
      {
        value: 'Brouillon',
        label: t('Brouillon'),
        variant: 'draft',
        flags: { delivered: false, validated: false, deployed: false },
      },
      {
        value: 'Livré',
        label: t('Livré'),
        variant: 'delivered',
        flags: { delivered: true, validated: false, deployed: false },
      },
      {
        value: 'En révision',
        label: t('En révision'),
        variant: 'revision',
        flags: {
          delivered: false,
          validated: false,
          deployed: false,
          inRevision: true,
        },
      },
      {
        value: 'Validé',
        label: t('Validé'),
        variant: 'validated',
        flags: { delivered: true, validated: true, deployed: false },
      },
      {
        value: 'En ligne',
        label: t('En ligne'),
        variant: 'online',
        flags: { delivered: true, validated: true, deployed: true },
      },
    ];

    const allowedValues = Array.isArray(allowedStatusValues) && allowedStatusValues.length > 0
      ? allowedStatusValues
      : (isConsultant ? ['Brouillon'] : null);

    if (!allowedValues) {
      return allOptions;
    }

    const allowed = new Set(allowedValues);
    return allOptions.filter((opt) => allowed.has(opt.value));
  }, [t, allowedStatusValues, isConsultant]);

  const { handleStatusChange, isUpdating } = useTextStatusChange(
    (id, newStatus) => {
      if (onStatusChange) {
        onStatusChange(id, newStatus);
      }

      // Popin de succès générique selon le nouveau statut
      let msgKey = 'Le statut a été mis à jour.';
      if (newStatus === 'Livré') {
        msgKey = 'Le texte a été livré avec succès.';
      } else if (newStatus === 'Brouillon') {
        msgKey = 'Le texte a été enregistré en brouillon avec succès.';
      } else if (newStatus === 'En révision') {
        msgKey = 'Le texte a été mis en révision avec succès.';
      } else if (newStatus === 'Validé') {
        msgKey = 'Le texte a été validé avec succès.';
      } else if (newStatus === 'En ligne') {
        msgKey = 'Le texte a été mis en ligne avec succès.';
      }
      setSuccessMsg(t(msgKey));
      setSuccessOpen(true);
    },
    (error, prevItem) => {
      if (isTextRevisionNotAllowedError(error)) {
        setIsRevisionErrorModalOpen(true);
      }
      if (onError) {
        onError(error, prevItem);
      }
    },
    onOptimisticUpdate
  );

  const getStatusMeta = (status) => {
    if (!status) {
      return { label: '-', variant: 'default' };
    }

    if (status.deployed) {
      return { label: t('En ligne'), variant: 'online' };
    }

    if (status.validated) {
      return { label: t('Validé'), variant: 'validated' };
    }

    if (status.inRevision) {
      return { label: t('En révision'), variant: 'revision' };
    }

    if (status.delivered) {
      return { label: t('Livré'), variant: 'delivered' };
    }

    return { label: t('Brouillon'), variant: 'draft' };
  };

  const getStatusIconByVariant = (variant) => {
    switch (variant) {
      case 'online':
        return '/Images/deployed-text-icon.png';
      case 'validated':
        return '/Images/validate-text-icon.png';
      case 'delivered':
        return '/Images/delivred-text-icon.png';
      case 'revision':
        return '/Images/en_revision.png';
      case 'draft':
      default:
        return '/Images/drafted-text-icon.png';
    }
  };

  useEffect(() => {
    if (!openStatusMenuId) return;

    const handleCloseMenu = () => {
      setOpenStatusMenuId(null);
      setStatusMenuCoords({ top: 0, left: 0, width: 0 });
    };

    document.addEventListener('click', handleCloseMenu);
    window.addEventListener('scroll', handleCloseMenu, true);

    return () => {
      document.removeEventListener('click', handleCloseMenu);
      window.removeEventListener('scroll', handleCloseMenu, true);
    };
  }, [openStatusMenuId]);

  const statusMeta = getStatusMeta(currentStatus);
  const isOpen = openStatusMenuId === textId;

  const handleMenuToggle = (e) => {
    e.stopPropagation();

    if (isOpen) {
      setOpenStatusMenuId(null);
      setStatusMenuCoords({ top: 0, left: 0, width: 0 });
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedMenuHeight = statusOptions.length * 36 + 24;
    const shouldOpenUp = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
    const offset = shouldOpenUp ? -2 : 2;
    const top = shouldOpenUp
      ? rect.top + window.scrollY - estimatedMenuHeight + offset
      : rect.bottom + window.scrollY - offset;
    const left = rect.left + window.scrollX;

    setStatusMenuCoords({
      top,
      left,
      width: rect.width,
    });
    setOpenStatusMenuId(textId);
  };

  const handleOptionClick = async (e, option) => {
    e.stopPropagation();

    // Fermer le menu immédiatement
    setOpenStatusMenuId(null);
    setStatusMenuCoords({ top: 0, left: 0, width: 0 });

    // Appeler le handler de changement de statut
    await handleStatusChange(textId, option.value, previousItem);
  };

  return (
    <>
      <div className="status-dropdown" onClick={(e) => e.stopPropagation()}>
        {design === 'iaSmart' ? (
          <button
            type="button"
            className="status-trigger-ia-smart"
            onClick={handleMenuToggle}
            disabled={isUpdating}
          >
            <img
              src={getStatusIconByVariant(statusMeta.variant)}
              alt={statusMeta.label}
              style={{ width: 16, height: 16 }}
            />
            <span>{statusMeta.label}</span>
            <img
              src="/Images/status-icon.png"
              alt="status icon"
              style={{ width: 16, height: 16 }}
            />
          </button>
        ) : (
          <button
            type="button"
            className={`status-trigger status-trigger--${statusMeta.variant || 'default'}`}
            onClick={handleMenuToggle}
            disabled={isUpdating}
          >
            {statusMeta.label}
            <svg viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        {isOpen && (
          <div
            className="status-dropdown-menu"
            style={{
              top: statusMenuCoords.top,
              left: statusMenuCoords.left,
              width: Math.max(statusMenuCoords.width || 0, 140),
            }}
          >
            {statusOptions.map((opt) => (
              <button
                type="button"
                key={opt.value}
                className={design === 'iaSmart'
                  ? 'status-option status-option--ia-smart'
                  : `status-option status-option--${opt.variant}`}
                onClick={(e) => handleOptionClick(e, opt)}
              >
                {design === 'iaSmart' && (
                  <img
                    src={getStatusIconByVariant(opt.variant)}
                    alt={opt.label}
                    style={{ width: 14, height: 14 }}
                  />
                )}
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <ErrorSuccesModal
        open={successOpen}
        handleClose={() => setSuccessOpen(false)}
        toggle={() => setSuccessOpen((p) => !p)}
        message={successMsg}
        success
      />

      <QuitProjectCreationModal
        isOpen={isRevisionErrorModalOpen}
        onClose={() => setIsRevisionErrorModalOpen(false)}
        variant="enrichment"
        title={t('Mise en révision impossible')}
        message={t(
          'Ce texte est validé ou mis en ligne. Il ne peut pas être envoyé en révision.'
        )}
        primaryButtonLabel={t('Fermer')}
        onPrimaryAction={() => setIsRevisionErrorModalOpen(false)}
        showSecondaryButton={false}
        showSuggestion={false}
      />
    </>
  );
};